import type { Moment } from '@this/src/lib/moment';
import moment from 'moment';

export type BugyoPostDealHistoryStatus = 'unstarted' | 'started' | 'succeeded' | 'failed';
export type BugyoPostDealHistoryMethod = 'separate';

export interface BugyoPostDealFailedLogResponseArgs {
  idx: number;
  issue_date: string | null;
  amount: number | null;
  ref_number: string | null;
  reason: string | null;
}

export interface BugyoPostDealHistoryResponseArgs {
  history_id: number;
  method: BugyoPostDealHistoryMethod;
  status: BugyoPostDealHistoryStatus | null;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
  total_count: number;
  success_count: number;
  failure_count: number;
  failed_logs: BugyoPostDealFailedLogResponseArgs[];
}

export interface BugyoPostDealFailedLog {
  idx: number;
  issueDate: string | null;
  amount: number | null;
  refNumber: string | null;
  reason: string | null;
}

export interface BugyoPostDealHistoryArgs {
  historyId: number;
  method: BugyoPostDealHistoryMethod;
  status: BugyoPostDealHistoryStatus | null;
  errorMessage: string | null;
  startedAt: Moment | null;
  finishedAt: Moment | null;
  totalCount: number;
  successCount: number;
  failureCount: number;
  failedLogs: BugyoPostDealFailedLog[];
}

export const convertBugyoPostDealHistoryResponseToArgs = (
  response: BugyoPostDealHistoryResponseArgs
): BugyoPostDealHistoryArgs => ({
  historyId: response.history_id,
  method: response.method,
  status: response.status,
  errorMessage: response.error_message || null,
  startedAt: response.started_at ? moment(response.started_at) : null,
  finishedAt: response.finished_at ? moment(response.finished_at) : null,
  totalCount: response.total_count,
  successCount: response.success_count,
  failureCount: response.failure_count,
  failedLogs: (response.failed_logs || []).map(log => ({
    idx: log.idx,
    issueDate: log.issue_date,
    amount: log.amount,
    refNumber: log.ref_number,
    reason: log.reason
  }))
});

class BugyoPostDealHistory {
  historyId: number;

  method: BugyoPostDealHistoryMethod;

  status: BugyoPostDealHistoryStatus | null;

  errorMessage: string | null;

  startedAt: Moment | null;

  finishedAt: Moment | null;

  totalCount: number;

  successCount: number;

  failureCount: number;

  failedLogs: BugyoPostDealFailedLog[];

  constructor(args: BugyoPostDealHistoryArgs) {
    this.historyId = args.historyId;
    this.method = args.method;
    this.status = args.status;
    this.errorMessage = args.errorMessage;
    this.startedAt = args.startedAt;
    this.finishedAt = args.finishedAt;
    this.totalCount = args.totalCount;
    this.successCount = args.successCount;
    this.failureCount = args.failureCount;
    this.failedLogs = args.failedLogs;
  }

  // 勘定奉行は登録のみで削除連携を持たないため固定
  get actionTypeText(): string {
    return '登録';
  }

  get methodText(): string {
    switch (this.method) {
      case 'separate':
        return '明細ごとに取引を作成';
      default:
        return '不明';
    }
  }

  get statusText(): string {
    switch (this.status) {
      case 'unstarted':
      case 'started':
        return '登録中';
      case 'succeeded':
        return '登録完了';
      case 'failed':
        return '登録失敗';
      default:
        return '不明';
    }
  }

  get startedAtText(): string {
    return this.startedAt ? this.startedAt.format('YYYY/MM/DD HH:mm') : '';
  }

  get finishedAtText(): string {
    return this.finishedAt ? this.finishedAt.format('YYYY/MM/DD HH:mm') : '';
  }

  get countText(): string {
    return `${this.successCount} / ${this.totalCount}`;
  }

  get inProgress(): boolean {
    return this.status === 'unstarted' || this.status === 'started';
  }
}

export default BugyoPostDealHistory;
